import {
  createFunction,
  FormCustomAction,
  FormCustomActionArgs,
  processCondition,
  sendNotification,
  useApiClient,
  useAsyncCache,
  useEntityMetadataIncludeExternal,
  useFormBaseMetadata,
  useModuleFederationContext,
  useTranslation,
  useUrlContext,
} from '@msdk/common'
import {
  CustomActionButtonAspect,
  CustomActionDefinition,
  EntityAcl,
  EntityContext,
  EntityMetadata,
  EntityModel,
  EntityOperation,
  FormMode,
  FormModel,
  FormNode,
  LogicalPath,
  UserPath,
} from '@msdk/definitions'
import { useEffect, useState } from 'react'

import { useComponentModal, useDialogInputModal, useDialogModal, useFormModal, useViewModal } from '../../hooks'
import { resolveItem } from '../../utils'
import { EnabledFormActions, FormActionWithData } from './enable-form-actions'
import {
  enableActivate,
  enableDeactivate,
  enableDelete,
  enableEdit,
  filterCustomActions,
  filterFormActions,
} from './filter-form-action-functions'

// eslint-disable-next-line @typescript-eslint/ban-types
function evaluateFunction(func: Function, entityContext: EntityContext) {
  return func({
    entity: entityContext,
    row: undefined,
    logic: new LogicalPath(),
    user: new UserPath(),
  })
}

function checkActionVisibility(action: CustomActionDefinition, entityContext: EntityContext | null): boolean {
  if (action.isVisible && entityContext) {
    const func = createFunction(processCondition(action.isVisible))
    return evaluateFunction(func, entityContext)
  }
  return true
}

export function useEnableFormActions(
  entityName: string,
  entity: EntityContext,
  formName: string,
  actions: CustomActionDefinition[],
  formMode: FormMode,
  acl: EntityAcl,
  formNode: FormNode,
  navigate: (url: string, sitemap?: string, additionalCtx?: Record<string, string>) => void,
  save: (actionId?: string) => Promise<EntityOperation>,
  reloadEntity: () => Promise<void>,
  entityLoaded: boolean,
  rebindEntity: (entity: EntityContext, pendingChanges?: boolean) => void,
  selectedTab?: number,
  isFirstTab?: boolean,
  isLastTab?: boolean,
  moveToTab?: (tabIndex: number) => void,
): EnabledFormActions {
  const logEnabled = false
  const model = useEntityMetadataIncludeExternal(entityName, formName) as EntityModel
  const formModel = model.getForm(formName) as FormModel
  const [actionInstances, setActionInstances] = useState<Record<string, FormCustomAction>>({})

  const apiClient = useApiClient()
  const formModal = useFormModal()
  const viewModal = useViewModal()
  const componentModal = useComponentModal()
  const dialogModal = useDialogModal()
  const dialogInputModal = useDialogInputModal()
  const asyncCache = useAsyncCache()
  const t = useTranslation()
  const baseMetadata = useFormBaseMetadata(entityName, formName) as EntityMetadata
  const modules = useModuleFederationContext()
  const module = modules.loadedModules[baseMetadata.msProvider]
  const { context } = useUrlContext()

  const formActions = filterFormActions(formMode, actions)
  const customAction = filterCustomActions(formMode, actions)

  useEffect(() => {
    if (entityLoaded) {
      const initActions = async () => {
        const instances: Record<string, FormCustomAction> = {}
        const initPromises: Promise<void>[] = []
        customAction.forEach((a) => {
          if (module) {
            const ctor = resolveItem(module, a.fullName)
            const args: FormCustomActionArgs = {
              apiClient,
              modals: { formModal, viewModal, componentModal, dialogModal, dialogInputModal },
              translate: t,
              entity,
              mode: formMode,
              navigate,
              save,
              asyncCache,
              reloadEntity,
              urlContext: context,
              config: [],
              rebindEntity,
              selectedTab,
              isFirstTab,
              isLastTab,
              moveToTab,
            }
            if (ctor) {
              instances[a.name] = new ctor(args)
              initPromises.push(instances[a.name].onInit())
            } else {
              sendNotification({
                message: t(
                  'CMPSR_SERVICE_CUSTOM_ACTION_FORM_ERROR',
                  'The form is missing some actions and it may not work properly.',
                ),
                status: 'error',
              })
              console.error(`[FormActions] - custom action not found, ${a.fullName}`)
            }
          }
        })
        await Promise.all(initPromises)
        setActionInstances(instances)
      }
      initActions().catch(console.error)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actions, entityLoaded])

  useEffect(() => {
    const cloned = { ...actionInstances }
    const keys = Object.keys(cloned)
    keys.forEach((k) => {
      const currentAction = cloned[k]
      currentAction.updateEntity(entity)
    })
    setActionInstances(cloned)
  }, [entity])

  useEffect(() => {
    const cloned = { ...actionInstances }
    const keys = Object.keys(cloned)
    keys.forEach((k) => {
      const currentAction = cloned[k]
      currentAction.updateTabsCallBack({
        selectedTab,
        isFirstTab,
        isLastTab,
        moveToTab,
      })
    })
    setActionInstances(cloned)
  }, [selectedTab, isFirstTab, isLastTab, moveToTab])

  useEffect(() => {
    const cloned = { ...actionInstances }
    const keys = Object.keys(cloned)
    keys.forEach((k) => {
      const currentAction = cloned[k]
      currentAction.updateSave(save)
    })
    setActionInstances(cloned)
  }, [save])

  if (!entityLoaded) {
    return {
      edit: false,
      deactivate: false,
      activate: false,
      log: false,
      delete: false,
      formActions: [],
      customActions: [],
      totalActionCount: 0,
      totalVisibleActionCount: 0,
    }
  }

  const visibleActions = customAction.filter((m) => actionInstances[m.name] && actionInstances[m.name].isVisible())
  const visibleFormActions = formActions.filter((action) => checkActionVisibility(action, entity))
  const instances: FormActionWithData[] = []

  // eslint-disable-next-line @typescript-eslint/no-shadow
  for (const customAction of visibleActions) {
    instances.push({
      action: actionInstances[customAction.name],
      name: customAction.name,
      idml: customAction.idml,
      description: customAction.description,
      icon: customAction.icon,
      aspect: customAction.buttonAspect as CustomActionButtonAspect,
      iconAlign: customAction.iconAlign,
      buttonAlign: customAction.buttonAlign,
      positionToDivider: customAction.positionToDivider,
      tooltip: customAction.tooltip,
    })
  }

  const standardActions = [
    enableActivate(formMode, entity, acl),
    enableEdit(formMode, acl),
    enableDelete(formMode, entity, acl, model),
    enableDeactivate(formMode, acl, entity, formModel, model),
  ].filter(Boolean).length
  const totalActionCount = standardActions + formActions.length + instances.length
  const totalVisibleActionCount = standardActions + visibleActions.length + visibleFormActions.length

  return {
    edit: enableEdit(formMode, acl),
    deactivate: enableDeactivate(formMode, acl, entity, formModel, model),
    activate: enableActivate(formMode, entity, acl),
    log: !!logEnabled,
    delete: enableDelete(formMode, entity, acl, model),
    formActions: visibleFormActions,
    customActions: instances,
    totalActionCount,
    totalVisibleActionCount,
  }
}
